/** Ad Marketplace advertiser portal (pages/marketplace/*: discover, booking-wizard, campaigns-dashboard) + venue ad-rental. */
export const marketplace = {
  // Standalone portal header (marketplace-header.tsx).
  header: {
    tagline: "Ad Marketplace",
    business: "Business",
    selectBusiness: "Select business",
    exitToSystem: "Exit to System",
    account: "Account",
    signOut: "Sign Out",
  },

  // Advertiser-context gate states (marketplace-gate.tsx).
  gate: {
    createBusiness: {
      title: "Name your business",
      description:
        "Set up your advertiser profile so you can book spots in other venues.",
      nameLabel: "Business name",
      namePlaceholder: "e.g. Bella's Coffee",
    },
    noAccess: {
      title: "Advertiser access needed",
      description:
        "None of your workspaces can advertise yet. Ask a workspace owner to grant you the “manage ad campaigns” permission, then come back here.",
      backToSystem: "Back to System",
    },
  },

  // Portal shell + sub-nav (index.tsx).
  portal: {
    sectionsLabel: "Advertiser sections",
    tabs: {
      discover: "Discover",
      campaigns: "Campaigns",
      creatives: "Creatives",
    },
    toast: {
      paymentReceived: "Payment received. Your ad is going live.",
    },
  },

  // Advertiser creatives library (creatives.tsx).
  creatives: {
    title: "Creatives",
    description: "Upload and manage your ad audio, video, and images, then run them in campaigns.",
    empty: "No creatives yet. Upload audio for speakers, or video / images for screens.",
  },

  // Venue picker used inside the campaign builder (venue-picker.tsx).
  venuePicker: {
    audio: "Audio",
    screen: "Screen",
    addVenue: "Add venue",
    added: "Added",
  },

  // Discover section — browse inventory (discover.tsx).
  discover: {
    filters: {
      search: "Search",
      searchPlaceholder: "Venue, location…",
      city: "City",
      cityPlaceholder: "Any city",
      channel: "Channel",
      anyChannel: "Any channel",
      maxPrice: "Max price / flight",
      minReach: "Min daily reach",
      any: "Any",
      clear: "Clear filters",
    },
    error: {
      title: "Couldn't load venues",
      description: "Something went wrong while loading marketplace inventory.",
    },
    empty: {
      filteredTitle: "No venues match your filters",
      emptyTitle: "No venues are renting ad time yet",
      filteredDescription:
        "Try widening your search: fewer filters, a higher price, or a lower reach.",
      emptyDescription:
        "When businesses open their screens and speakers to advertisers, they'll show up here.",
    },
    venueCard: {
      reach: "~{count} people / day",
      reachUnknown: "Reach not estimated",
      priceCaption: "from / flight",
      book: "Book this venue",
    },
  },

  // Booking wizard (booking-wizard.tsx).
  wizard: {
    steps: {
      creative: "Creative",
      flight: "Flight",
      review: "Review",
    },
    bookTitle: "Book {name}",
    creative: {
      heading: "Choose your ad audio",
      description:
        "Pick a ready creative or upload a new spot. Only ready audio can be booked.",
      empty: "No ad audio yet. Upload your first spot below.",
      uploadingPercent: "Uploading… {percent}%",
      uploadButton: "Upload new ad audio",
      invalidType: "Please choose an audio file (MP3, WAV, …).",
    },
    flight: {
      starts: "Starts",
      ends: "Ends",
      datesInvalid: "End date must be on or after the start date.",
      spotsPerHour: "Spots per hour",
      spotsPerHourHint: "How often your spot plays each hour (1–12).",
      price: "Price (€)",
      venueFloorHint:
        "Venue floor: {price}. You can offer more to stand out.",
    },
    review: {
      venue: "Venue",
      location: "Location",
      creative: "Creative",
      spotsPerHour: "Spots / hour",
      flight: "Flight",
      price: "Price",
      approvalNoteBefore:
        "We send this booking to the venue for approval. You'll pay from",
      approvalNoteEmphasis: "My campaigns",
      approvalNoteAfter:
        "only after the venue approves it. Nothing is charged now.",
      termsBefore: "I agree to the",
      termsLink: "OmniMedia advertiser terms",
      termsAfter:
        ", and confirm I hold the rights to this creative and that it meets the venue's content standards.",
    },
    submit: "Book & send for approval",
    toast: {
      uploaded: "\"{title}\" uploaded",
      uploadFailed: "Upload failed",
      uploadToStorageFailed: "Upload to storage failed",
      sentForApproval: "Sent to the venue for approval",
      submitFailedTitle: "Could not submit booking",
      submitFailedDescription: "Please try again in a moment.",
    },
  },

  // Campaigns dashboard + builder (campaigns-dashboard.tsx).
  campaigns: {
    title: "Your campaigns",
    new: "New campaign",
    empty: "No campaigns yet. Create one, add your creatives, then pick venues to run it.",
    back: "All campaigns",
    addVenues: "Add venues",
    addVenuesHint: "Pick venues to run this campaign, then set the flight and price for each.",
    launch: "Launch campaign",
    placements: "Venues in this campaign",
    noPlacements: "No venues yet. Add one to start running this campaign.",
    pay: "Pay",
    cancel: "Cancel",
    table: {
      name: "Campaign",
      venues: "Venues",
      spend: "Spend",
      venue: "Venue",
      flight: "Flight",
      price: "Price",
      payment: "Payment",
      actions: "Actions",
    },
    create: {
      title: "New campaign",
      description: "Name your campaign and pick the creatives it will run.",
      name: "Campaign name",
      objective: "Objective (optional)",
      creatives: "Creatives",
      noCreatives: "No creatives yet. Upload some in the Creatives tab first.",
      submit: "Create campaign",
    },
    config: {
      creative: "Creative to run here",
      noCompatibleCreative:
        "None of this campaign's creatives fit this venue (audio venues need audio; screens need video or image).",
      add: "Add venue",
    },
    launchDialog: {
      title: "Launch campaign",
      description:
        "Each venue reviews and approves your ad, then you pay per venue. Nothing is charged now.",
    },
    resubmit: {
      action: "Fix and resubmit",
      title: "Fix and resubmit",
      description:
        "Adjust the flight, price, or creative and create a fresh draft for this venue. The rejected placement stays for reference; launch the campaign to send the new draft for approval.",
      rejectionLabel: "Venue's reason",
      noReason: "The venue didn't give a reason.",
      submit: "Create new draft",
    },
    toast: {
      created: "Campaign created",
      createFailed: "Could not create campaign",
      venueAdded: "Venue added to the campaign",
      venueAddFailed: "Could not add venue",
      launched: "Sent to venues for approval",
      launchFailed: "Could not launch campaign",
      cancelled: "Placement cancelled",
      payFailed: "Checkout unavailable. The venue may not have finished payout setup.",
      resubmitted: "New draft created. Launch the campaign to send it for approval",
      resubmitFailed: "Could not resubmit this placement",
    },
  },

  // Venue-side ad rental (ad-rental.tsx).
  adRental: {
    title: "Ad rental",
    description:
      "Rent your venues' in-store ad time to other businesses, and get paid out automatically.",
    payouts: {
      title: "Payouts",
      enabled:
        "Payouts enabled. You'll receive advertiser payments automatically.",
      disabled:
        "Set up Stripe payouts to accept paid bookings. Advertisers can't be charged until this is complete.",
      continueSetup: "Continue payout setup",
      setUp: "Set up payouts",
    },
    earnings: {
      title: "Earnings",
      netPayout: "Net payout",
      gross: "Gross",
      platformFee: "Platform fee",
      paidBookings: "Paid bookings",
    },
    inventory: {
      title: "Your ad inventory",
      selectVenue: "Select a venue…",
      floorPriceLabel: "Floor price / flight (€)",
      headlinePlaceholder: "Headline (optional)",
      listVenue: "List venue",
      empty:
        "No venues listed yet. List a venue above to start renting ad time.",
      table: {
        venue: "Venue",
        floorPrice: "Floor price",
        actions: "Actions",
      },
      paused: "Paused",
      pause: "Pause listing",
      resume: "Resume listing",
    },
    bookings: {
      title: "Booking requests",
      empty: "No booking requests yet.",
      table: {
        advertiser: "Advertiser",
        venue: "Venue",
        flight: "Flight",
        price: "Price",
        actions: "Actions",
      },
      spotsPerHour: "{count}/hr",
      approve: "Approve",
      reject: "Reject",
      preview: "Preview",
    },
    rejectDialog: {
      title: "Reject booking",
      description:
        "Optionally tell the advertiser why. This is emailed to them.",
      placeholder: "Reason (optional)",
    },
    previewDialog: {
      title: "Ad creative preview",
      unavailable: "Preview is unavailable for this creative.",
    },
    toast: {
      pickVenue: "Pick a venue first",
      listed: "Venue listed for ad rental",
      listFailed: "Could not list venue",
      payoutUnavailableTitle: "Payout setup unavailable",
      payoutUnavailableDescription: "Stripe may not be configured yet.",
      approved: "Booking approved",
      approveFailed: "Approval failed",
      rejected: "Booking rejected",
      rejectFailed: "Rejection failed",
      delisted: "Venue delisted",
    },
  },
  admin: {
    title: "Marketplace admin",
    description: "Operator oversight of every ad booking across all workspaces.",
    allBookings: "All bookings",
    empty: "No ad bookings yet.",
    table: {
      advertiser: "Advertiser",
      host: "Host",
      venue: "Venue",
      price: "Price",
      status: "Status",
      payment: "Payment",
      actions: "Actions",
    },
    refund: "Refund",
    end: "End",
    toast: {
      refunded: "Booking refunded",
      ended: "Flight ended",
      actionFailed: "Action failed",
    },
  },

  // Delivery-compliance report (per booking/placement, M3 T1.1).
  report: {
    action: "Report",
    title: "Delivery report",
    plays: "Plays",
    completed: "Completed",
    expected: "Expected",
    expectedPerDevice: "{perDevice} per device × {devices} devices",
    deliveryRate: "Delivery",
    day: "Day",
    noPlays: "No plays recorded yet.",
    caveat:
      "Days are venue-local ({timezone}). Expected counts cover the booked window at the booked cadence, clamped to the venue's trading hours (or the platform's default daytime window when the venue has none). If the venue changes its hours mid-flight, expected counts follow the current hours.",
    loadFailed: "Couldn't load the report.",
  },
} as const;
